import type { UnwrapNestedRefs } from "vue";
import Qs from "qs";
import request from "../../utils/request";
import cloneDeep from "lodash-es/cloneDeep";
import merge from "lodash-es/merge";
import isFunction from "lodash-es/isFunction";
import { buildProps } from "../../utils/props";
import type { Method } from "axios";
import { onMounted, reactive, ref, watch } from "vue";
import type { ExtractPropTypes, PropType, Ref, SetupContext } from "vue";
import { FetchDataTypeConf, FetchListTypeConf, FetchTypeConf } from "./types";
import type { AfterSuccessFuncType, BeforeFetchDataFilterType, FetchDataConfType, FetchDataFilterType, FetchDataTypeConfKeyType, FetchListTypeConfKeyType, FetchTypeConfKeyType, FindInfoOperateType, IsSuccessType, MergeDataFilterType, MergeResponeDataType, OnLoadDataSuccessType, ResDataFilterType, UrlFunctionType, AfterErrorFuncType, FetchApiInterface } from "./types";
import { isEqual } from "lodash-es";

/*
  loadDataMix * 常用的查询详情的接口封装；
*/

// loadDataMix组件props定义
export const findInfoProps = buildProps({
  /**
   * 配置请求实例,promise fetch对象
   */
  fetchApi: {
    type: [Boolean, Function as PropType<FetchApiInterface>],
    default: false,
  },
  /**
   * 是否禁用请求
   */
  disabledFetch: {
    type: Boolean,
    default: false,
  },
  /**
   * 配置查询的url
   */
  url: {
    type: [String, Function as PropType<UrlFunctionType>],
    default: "",
  },
  /**
   * 配置查询的方式
   */
  fetchType: {
    type: String as PropType<Method>,
    values: Object.keys(FetchTypeConf).map(
      (key) => FetchTypeConf[key as FetchTypeConfKeyType]
    ),
    default: FetchTypeConf.GET,
  },
  /**
   * 配置查询带的数据
   */
  fetchData: {
    type: [Object, Array, String],
    default: () => ({}),
  },
  /**
   * 配置查询的前置数据过滤方法
   */
  beforeFetchDataFilter: {
    type: Function as PropType<BeforeFetchDataFilterType>,
    default: (res: unknown): unknown => {
      return res;
    },
  },
  /**
   * 配置查询的数据过滤方法
   */
  fetchDataFilter: {
    type: Function as PropType<FetchDataFilterType>,

    default: (res: unknown): unknown => res,
  },
  /**
   * 配置请求的数据的提交方式，可以为json或者form
   */
  fetchDataType: {
    type: String,
    values: Object.keys(FetchDataTypeConf).map(
      (key) => FetchDataTypeConf[key as FetchDataTypeConfKeyType]
    ),
    default: FetchDataTypeConf.JSON,
  },
  /**
   * 配置请求成功后的数据回调
   */
  onLoadDataSuccess: {
    type: Function as PropType<OnLoadDataSuccessType>,
    default: (res: unknown) => res,
  },
  /**
   * 配置请求成功后返回的数据是否是成功数据
   */
  isSuccess: {
    type: Function as PropType<IsSuccessType>,
    default: (res: unknown) => !!res,
  },
  /**
   * 配置返回的数据过滤方法
   */
  resDataFilter: {
    type: Function as PropType<ResDataFilterType>,
    default: (res: unknown) => res,
  },
  /**
   * 返回的数据的初始值
   */
  modelValue: {
    type: [Object, Array],
    default: () => ({}),
  },
  /**
   * 配置列表，可一次查询多个接口数据
   */
  configList: {
    type: [Boolean, Array as PropType<Array<Partial<FetchDataConfType>>>],
    default: false,
  },
  /**
   * 配置 一次查询多个接口数据的回调类型，是各自回调或是同一执行一个回调
   */
  fetchListType: {
    type: String,
    values: Object.keys(FetchListTypeConf).map(
      (key) => FetchListTypeConf[key as FetchListTypeConfKeyType]
    ),
    default: FetchListTypeConf.ALL,
  },
});

export type FindInfoPropsType = ExtractPropTypes<typeof findInfoProps>;

export default function (
  props: FindInfoPropsType,
  ctx: SetupContext<unknown>,
  afterSuccessFunc: AfterSuccessFuncType,
  afterErrorFunc?: AfterErrorFuncType
): FindInfoOperateType {
  let timer: any;
  const loading: Ref<boolean> = ref(false);
  const findInfoState: UnwrapNestedRefs<{ infoData: any }> = reactive({
    infoData: cloneDeep(props.modelValue),
  });
  const stateFetchData: UnwrapNestedRefs<unknown> = reactive({});
  onMounted(() => {
    if (!props.disabledFetch) loadData();
  });
  watch(
    () => props.fetchData,
    (newValue: any, oldValue: any) => {
      if (!isEqual(newValue, oldValue) && !props.disabledFetch) {
        clearTimeout(timer);
        timer = setTimeout(() => {
          loadData();
        }, 500);
      }
    }
  );
  // methods start
  /**
     * 拷贝一个全新数据的方法
  
     */
  function getJson(d: any): any {
    return cloneDeep(d);
  }

  /**
  //    * 重新数据的方法
  //    */
  function resetData(data: any): void {
    loading.value = false;
    if (
      !props.url &&
      props.configList &&
      (props.configList as Array<unknown>).length &&
      props.fetchListType == "every"
    ) {
      // 当数据是各自请求时合并
      findInfoState.infoData = merge(cloneDeep(findInfoState.infoData), data);
    } else {
      findInfoState.infoData = merge(data);
    }
  }

  /**
   * 生成请求的方法
   * @param  {Object} conf 配置的对象
   * @param  {Number}} index 单个配置的 idx
   */
  function makeConf(conf: FetchDataConfType, index?: number): Promise<any> {
    const stateFetchData = conf.stateFetchData; // 初始化带有的数据
    const fetchData = conf.fetchData; // 配置请求带有的数据
    const beforeFetchDataFilter =
      conf.beforeFetchDataFilter || props.beforeFetchDataFilter; // 当请求时候的预处理配置数据过滤方法，一般用来全局配置
    const fetchDataFilter = conf.fetchDataFilter || props.fetchDataFilter; // 请求时候的数据过滤方法，一般用来动态配置请求方法
    const fetchType = conf.fetchType as Method; // 请求类型
    const fetchDataType = conf.fetchDataType; // 请求的数据类型，现在只支持form和json形式
    let fetchDataAll = {
      ...getJson(fetchData),
      ...getJson(stateFetchData),
    };

    fetchDataAll = beforeFetchDataFilter(fetchDataAll);
    const setUrl = isFunction(conf.url) ? conf.url(fetchDataAll) : conf.url;
    fetchDataAll = fetchDataFilter(fetchDataAll);

    if (fetchType == FetchTypeConf.GET && typeof conf.fetchApi != "function") {
      fetchDataAll = {
        params: fetchDataAll,
      };
    }
    let setConf = {};
    if (fetchDataType == FetchDataTypeConf.FORM) {
      setConf = {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        transformRequest: [
          function (data: unknown) {
            data = Qs.stringify(data);
            return data;
          },
        ],
      };
    }
    const fetchApi: Promise<any> =
      typeof conf.fetchApi == "function"
        ? conf.fetchApi(fetchDataAll, setConf)
        : request({
            url: setUrl,
            method: props.fetchType as Method,
            [props.fetchType == FetchTypeConf.GET ? "params" : "data"]:
              fetchDataAll,
            ...setConf,
          });
    return fetchApi.then((res: any): MergeResponeDataType | any => {
      if (typeof index == "number") {
        const mergeResData: MergeResponeDataType = {
          ...res,
          data: res.data,
          confName: conf.name,
          confIdx: index,
        };
        return mergeResData;
      } else {
        return res;
      }
    });
  }
  /**
  //    * 请求数据的方法
  //    */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loadData = function (): void {
    console.log("loading.....");

    if (props.url || props.fetchApi) {
      // 单个请求配置
      loading.value = true;
      makeConf({
        url: props.url,
        fetchApi: props.fetchApi,
        fetchData: props.fetchData,
        fetchType: props.fetchType,
        fetchDataType: props.fetchDataType,
        fetchDataFilter: props.fetchDataFilter,
        beforeFetchDataFilter: props.fetchDataFilter,
        stateFetchData: stateFetchData,
      })
        .then(onFetchSuccess)
        .catch(onFetchError);
    } else if (
      !!props.configList &&
      (props.configList as Array<FetchDataConfType>).length
    ) {
      const configList = props.configList as Array<FetchDataConfType>;
      // 多个请求配置
      loading.value = true;
      const PromiseList: Array<Promise<any>> = [];

      configList.map((conf: FetchDataConfType, i: number) => {
        PromiseList.push(makeConf(conf, i));
      });
      if (props.fetchListType == FetchListTypeConf.ALL) {
        // 同时返回完请求
        Promise[FetchListTypeConf.ALL](PromiseList)
          .then(mergeDataFilter)
          .then(onFetchSuccess)
          .catch(onFetchError);
      } else if (props.fetchListType == FetchListTypeConf.EVERY) {
        // 各自返回完请求
        PromiseList.map((PromiseItem) => {
          PromiseItem.then(everyFetchDataFilter)
            .then(onFetchSuccess)
            .catch(onFetchError);
        });
      }
    }
  };
  /**
   * 合并全部请求的数据 方法
   * @param  {Object} res 返回的数据
   */
  function mergeDataFilter(
    res: Array<MergeResponeDataType>
  ): MergeDataFilterType {
    //  全部请求的判断方法
    const resData: MergeDataFilterType = {
      success: true,
      data: {},
    };
    res.map((o) => {
      resData.data[o.confName || `data${o.confIdx}`] = o.data;
    });

    return resData;
  }
  /**
   * 合并各自请求的数据 方法
   * @param  {Object} res 返回的数据
   */
  function everyFetchDataFilter(
    res: MergeResponeDataType
  ): MergeDataFilterType {
    const resData: MergeDataFilterType = {
      success: true,
      data: {},
    };

    resData.data[res.confName || `data${res.confIdx}`] = res.data;

    return resData;
  }
  /**
   * 成功后的回调
   * @param  {Object} res 返回的数据
   */
  function onFetchSuccess(res: any) {
    res = props.resDataFilter(res);
    loading.value = false;

    if (props.isSuccess(res)) {
      resetData(res.data);
    }
    if (afterSuccessFunc) {
      afterSuccessFunc(res);
    }
  }
  /**
   * 失败后的回调
   * @param  {Object} err 失败返回的数据
   */
  function onFetchError(err: Error) {
    resetData({});
    if (isFunction(afterErrorFunc)) {
      afterErrorFunc(err);
    }
    throw err;
  }
  return {
    loading,
    findInfoState,
    stateFetchData,
    loadData,
  };
}
